import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useAuthStore } from '@/store/auth';
import { useThemeStore } from '@/store/theme';
import { Logo } from '@/components/shared/Logo';
import { Sun, Moon, Monitor, LogOut, Menu, X } from 'lucide-react';
import { cn } from '@/utils/cn';
import { NAV_ITEMS } from './nav';
import { NotificationPanel } from '@/components/shared/NotificationPanel';


export function TopBar() {
  const { user, logout } = useAuthStore();
  const { theme, setTheme } = useThemeStore();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const themeOptions = [
    { value: 'light' as const, icon: Sun },
    { value: 'dark' as const, icon: Moon },
    { value: 'system' as const, icon: Monitor },
  ];

  return (
    <>
      <header className="flex h-16 items-center justify-between border-b border-border px-4 md:px-6">
        <div className="flex items-center gap-3 lg:hidden">
          <button onClick={() => setMobileMenuOpen(true)} className="rounded-lg p-2 hover:bg-accent">
            <Menu className="h-5 w-5" />
          </button>
          <Logo size="sm" />
        </div>

        <div className="flex-1" />

        <div className="flex items-center gap-2 md:gap-3">
          <div className="hidden sm:flex items-center rounded-lg border border-border p-0.5">
            {themeOptions.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setTheme(opt.value)}
                className={cn(
                  'rounded-md p-1.5 transition-colors',
                  theme === opt.value ? 'bg-accent text-foreground' : 'text-muted-foreground hover:text-foreground'
                )}
              >
                <opt.icon className="h-4 w-4" />
              </button>
            ))}
          </div>

          <NotificationPanel />

          {user && (
            <div className="flex items-center gap-2 md:gap-3 border-l border-border pl-2 md:pl-3">
              <img
                src={user.avatarUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(user.name)}&background=3b82f6&color=fff`}
                alt={user.name}
                className="h-8 w-8 rounded-full"
              />
              <div className="hidden md:block">
                <p className="text-sm font-medium">{user.name}</p>
                <p className="text-xs text-muted-foreground">{user.email}</p>
              </div>
              <button onClick={logout} className="rounded-lg p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground">
                <LogOut className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
      </header>

      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileMenuOpen(false)} />
          <div className="relative flex h-full w-72 flex-col bg-card shadow-xl animate-in slide-in-from-left">
            <div className="flex h-16 items-center justify-between border-b border-border px-6">
              <Logo size="sm" />
              <button onClick={() => setMobileMenuOpen(false)} className="rounded-lg p-1.5 hover:bg-accent">
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav className="flex-1 space-y-1 p-4">
              {NAV_ITEMS.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
                      isActive
                        ? 'bg-primary/10 text-primary'
                        : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                    )
                  }
                >
                  <item.icon className="h-5 w-5" />
                  {item.label}
                </NavLink>
              ))}
            </nav>
            <div className="border-t border-border p-4">
              <div className="flex items-center justify-between rounded-lg bg-accent/50 p-3">
                <span className="text-xs text-muted-foreground">Theme</span>
                <div className="flex gap-1">
                  {themeOptions.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => setTheme(opt.value)}
                      className={cn(
                        'rounded-md p-1.5 transition-colors',
                        theme === opt.value ? 'bg-background text-foreground' : 'text-muted-foreground'
                      )}
                    >
                      <opt.icon className="h-3.5 w-3.5" />
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
